<template>
  <div class="h-full flex flex-col bg-[#f8fafc] gap-4 overflow-auto pr-2">
    <div class="print-hide bg-white rounded-xl shadow-sm border border-slate-200 overflow-visible border-t-4 border-t-[#0d4169]">
      <div class="px-6 pt-5 pb-3">
        <h2 class="text-base font-bold text-[#0d4169]">Non-Stockable Report</h2>
      </div>
      <form @submit.prevent="generateReport" class="px-6 pb-4 pt-2">
        <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Start Date <span v-if="isStartDateRequired" class="text-red-500">*</span></label>
            <input v-model="filters.startDate" type="date" :required="isStartDateRequired" class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm" />
          </div>
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">End Date</label>
            <input v-model="filters.endDate" type="date" class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm" />
          </div>
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Category</label>
            <select v-model="filters.categoryId" class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer">
              <option :value="null">All</option>
              <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Item</label>
            <input v-model="filters.itemQuery" type="text" placeholder="Search item/product name..." list="ns-report-item-suggestions" class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm" />
            <datalist id="ns-report-item-suggestions">
              <option v-for="p in departmentProducts" :key="`p-${p.id}`" :value="p.name" />
            </datalist>
          </div>
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">NS Number</label>
            <input v-model="filters.nsNo" type="text" placeholder="e.g. NS-0001" class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm" />
          </div>
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">PR Number</label>
            <input v-model="filters.prNo" type="text" placeholder="e.g. PR-2024..." class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm" />
          </div>
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Supplier</label>
            <select v-model="filters.supplierId" class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer">
              <option :value="null">All</option>
              <option v-for="s in suppliers" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Department</label>
            <select v-model="filters.departmentId" class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer">
              <option :value="null">All</option>
              <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
            </select>
          </div>
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Taxable</label>
            <select v-model="filters.taxableFilter" class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer">
              <option value="all">All</option>
              <option value="1">Taxable</option>
              <option value="0">Non Taxable</option>
            </select>
          </div>
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Is Ingredient</label>
            <select v-model="filters.ingredientFilter" class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer">
              <option value="all">All</option>
              <option value="1">Ingredient</option>
              <option value="0">Material</option>
            </select>
          </div>
        </div>
        <div class="mt-3 pt-3 pr-8 border-t border-slate-100 flex justify-between items-center gap-3">
          <button
            type="button"
            @click="handlePrintReport"
            class="px-4 py-1.5 bg-slate-200 text-slate-700 text-sm font-bold rounded-md shadow-md hover:bg-slate-300 transition-all active:scale-95 disabled:opacity-60 flex items-center gap-2"
            :disabled="loading"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 9V2h12v7"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            <span>Print</span>
          </button>
          <button type="submit" class="px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95">
            <div v-if="loading" class="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"></div>
            <span v-else>Generate </span> 
          </button>
        </div>
      </form>
    </div>

    <div class="w-full overflow-x-auto shadow-sm rounded-xl pa-1 print-container nonstockable-table-wrap">
      <div class="hidden print:block mb-6">
        <div class="print-doc-header print-doc-header-grid">
          <div class="print-doc-left">
            <div class="print-company-block">
              <img
                v-if="printCompanyLogo"
                :src="printCompanyLogo"
                :alt="printCompanyName || 'Company Logo'"
                class="print-company-logo"
              />
              <div v-else class="print-company-logo-fallback">Logo</div>
              <div class="print-company-text">
                <div class="print-company-name">{{ printCompanyName }}</div>
              </div>
            </div>
          </div>
          <h1 class="print-doc-title text-xl font-bold text-center mb-2">Non-Stockable Report</h1>
          <div class="print-doc-meta print-doc-right">
            <div><strong>Date:</strong> {{ new Date().toLocaleDateString() }}</div>
          </div>
        </div>
        <hr class="my-4 border-black" />
      </div>
      <table class="w-full border-collapse bg-white overflow-hidden print-table">
        <thead>
          <tr>
            <th class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider">#</th>
            <th @click="sortBy('nsNo')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Ns No <span v-if="sortKey === 'nsNo'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('prNo')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Pr No <span v-if="sortKey === 'prNo'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('supplierName')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Supplier <span v-if="sortKey === 'supplierName'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('departmentName')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Department <span v-if="sortKey === 'departmentName'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('categoryName')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Category <span v-if="sortKey === 'categoryName'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('itemName')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Item Name <span v-if="sortKey === 'itemName'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('quantity')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Quantity <span v-if="sortKey === 'quantity'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('volume')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Volume <span v-if="sortKey === 'volume'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('unitPrice')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Unit Price <span v-if="sortKey === 'unitPrice'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('totalValue')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Total Price <span v-if="sortKey === 'totalValue'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('isTaxable')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Taxable <span v-if="sortKey === 'isTaxable'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('createdBy')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Created By <span v-if="sortKey === 'createdBy'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('createdAt')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Created At <span v-if="sortKey === 'createdAt'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(g, i) in sortedRows" :key="g.key" class="bg-white border-b">
            <td class="px-2 py-1 text-slate-700">{{ i + 1 }}</td>
            <td class="px-2 py-1 text-slate-700">{{ g.nsNo }}</td>
            <td class="px-2 py-1 text-slate-700">{{ g.prNo || '-' }}</td>
            <td class="px-2 py-1 text-slate-700">{{ g.supplierName || '-' }}</td>
            <td class="px-2 py-1 text-slate-700">{{ g.departmentName || '-' }}</td>
            <td class="px-2 py-1 text-slate-700">{{ g.categoryName || '-' }}</td>
            <td class="px-2 py-1" :class="g.isProduct ? 'text-green-600 font-semibold' : 'text-slate-700'">{{ g.itemName || '-' }}</td>
            <td class="px-2 py-1 text-slate-700">{{ g.quantity }}</td>
            <td class="px-2 py-1 text-slate-700">{{ g.volume }}</td>
            <td class="px-2 py-1 text-slate-700">{{ g.unitPrice }}</td>
            <td class="px-2 py-1 text-slate-700">{{ g.totalValue }}</td>
            <td class="px-2 py-1 text-slate-700">
              <span class="px-2 py-0.5 rounded font-bold border" :class="g.isTaxable ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'">
                {{ g.isTaxable ? 'Yes' : 'No' }}
              </span>
            </td>
            <td class="px-2 py-1 text-slate-700">{{ g.createdBy || '-' }}</td>
            <td class="px-2 py-1 text-slate-700">{{ g.createdAt || '-' }}</td>
          </tr>
          <tr v-if="sortedRows.length > 0" class="bg-slate-50 border-t-2 border-slate-300">
            <td colspan="9"></td>
            <td class="px-2 py-1 text-right font-bold text-slate-700">Total:</td>
            <td class="px-2 py-1 font-bold text-[#0d4169]">{{ totalPriceSum }}</td>
            <td colspan="3"></td>
          </tr>
          <tr v-if="sortedRows.length === 0">
            <td colspan="14" class="px-4 py-10 text-center text-slate-400 italic text-sm">No data found for the selected filters.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import store from '../../../store'
import { apiFetch } from '../../../utils/api'

const suppliers = ref([])
const departments = ref([])
const categories = ref([])
const departmentProducts = ref([])
const report = ref([])
const loading = ref(false)
const sortKey = ref('nsNo')
const sortOrder = ref('asc')

const filters = ref({
  startDate: "",
  endDate: '',
  nsNo: '',
  prNo: '',
  supplierId: null,
  departmentId: null,
  categoryId: null,
  itemQuery: '',
  taxableFilter: 'all',
  ingredientFilter: 'all'
})

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)
const printCompanyName = computed(() =>
  currentUser.value?.company?.name ||
  currentUser.value?.companyName ||
  currentUser.value?.company_name ||
  'Company'
)
const printCompanyLogo = computed(() =>
  currentUser.value?.company?.logo_url ||
  currentUser.value?.companyLogo ||
  currentUser.value?.company_logo ||
  ''
)
const isStartDateRequired = computed(() =>
  String(filters.value.itemQuery || '').trim() === '' &&
  String(filters.value.nsNo || '').trim() === '' &&
  String(filters.value.prNo || '').trim() === ''
)

async function loadLookups() {
  if (!companyId.value && !branchId.value) return
  try {
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const [supRes, depRes, catRes] = await Promise.all([
      apiFetch(branchId.value ? `/suppliers/branch/${branchId.value}` : `/suppliers/company/${companyId.value}`),
      apiFetch(`/departments?${query}`),
      apiFetch(`/categories?${query}`)
    ])
    if (supRes.ok) suppliers.value = await supRes.json()
  suppliers.value.sort((a, b) => a.name.localeCompare(b.name))

    if (depRes.ok) departments.value = await depRes.json()
     departments.value.sort((a, b) => a.name.localeCompare(b.name))
    if (catRes.ok) categories.value = await catRes.json()
         categories.value.sort((a, b) => a.name.localeCompare(b.name))
  } catch (err) {
    console.error(err)
  }
}

async function loadProductsByDepartment() {
  departmentProducts.value = []
  if (!companyId.value && !branchId.value) return
  try {
    const params = new URLSearchParams()
    if (branchId.value) params.set('branchId', branchId.value)
    else if (companyId.value) params.set('companyId', companyId.value)
    if (filters.value.departmentId) params.set('departmentId', filters.value.departmentId)
    if (filters.value.ingredientFilter === '1') params.set('is_ingredient', '1')
    else if (filters.value.ingredientFilter === '0') params.set('is_ingredient', '0')
    const res = await apiFetch(`/products?${params.toString()}`)
    if (res.ok) {
      const data = await res.json().catch(() => [])
      departmentProducts.value = Array.isArray(data) ? data : []
    }
  } catch (err) {
    console.error('Failed to load products by department', err)
  }
}

async function generateReport() {
  const hasLikeSearch = !isStartDateRequired.value
  if (!hasLikeSearch && !filters.value.startDate) {
    alert('Start date is required unless Item, NS Number, or PR Number is filled')
    return
  }
  loading.value = true
  const queryParts = []
  if (branchId.value) queryParts.push(`branchId=${branchId.value}`)
  else if (companyId.value) queryParts.push(`companyId=${companyId.value}`)
  if (!hasLikeSearch && filters.value.startDate) queryParts.push(`startDate=${filters.value.startDate}`)
  if (!hasLikeSearch && filters.value.endDate) queryParts.push(`endDate=${filters.value.endDate}`)
  if (filters.value.nsNo) queryParts.push(`nsNo=${encodeURIComponent(filters.value.nsNo)}`)
  if (filters.value.prNo) queryParts.push(`prNo=${encodeURIComponent(filters.value.prNo)}`)
  if (filters.value.supplierId) queryParts.push(`supplierId=${filters.value.supplierId}`)
  if (filters.value.departmentId) queryParts.push(`departmentId=${filters.value.departmentId}`)
  if (filters.value.categoryId) queryParts.push(`categoryId=${filters.value.categoryId}`)
  if (filters.value.itemQuery) queryParts.push(`itemQuery=${encodeURIComponent(filters.value.itemQuery)}`)
  if (filters.value.taxableFilter === '1') queryParts.push('isTaxable=true')
  else if (filters.value.taxableFilter === '0') queryParts.push('isTaxable=false')
  if (filters.value.ingredientFilter === '1') queryParts.push('is_ingredient=1')
  else if (filters.value.ingredientFilter === '0') queryParts.push('is_ingredient=0')
  const q = queryParts.length ? `?${queryParts.join('&')}` : ''
  try {
    const res = await apiFetch(`/non-stockable/report${q}`)
    report.value = res.ok ? await res.json().catch(() => []) : []
  } catch (err) {
    console.error('Failed to fetch non-stockable report', err)
    report.value = []
  }
  finally {
    loading.value = false
  }
}

function parseCsvNums(v) {
  if (v == null || v === '') return []
  return String(v).split(',').map(s => Number(String(s).trim())).filter(n => Number.isFinite(n))
}

const flattenedRows = computed(() => {
  const out = []
  for (const ns of report.value || []) {
    const base = {
      nsNo: ns?.nsNo || '-',
      prNo: ns?.prNo || '-',
      supplierName: ns?.supplier?.name || '-',
      createdBy: ns?.creator?.full_name || '-',
      createdAt: ns?.created_at ? new Date(ns.created_at).toISOString().split('T')[0] : '-'
    }
    const items = Array.isArray(ns?.items) ? ns.items : []
    for (const it of items) {
      const itemName = it?.item?.itemName || it?.product?.name || '-'
      const isProduct = !!it?.product || Number(it?.productId || 0) > 0
      const departmentName = ns?.department?.name || (isProduct ? (it?.product?.department?.name || '-') : '-')
      const categoryName = isProduct ? '-' : (it?.item?.category?.name || ns?.category?.name || '-')
      const qtys = parseCsvNums(it?.quantityDetails)
      const vols = parseCsvNums(it?.variantDetails)
      const prices = parseCsvNums(it?.unitPriceDetails)
      const hasSplit = qtys.length > 0
      if (hasSplit) {
        const len = qtys.length
        for (let i = 0; i < len; i++) {
          const q = Number(qtys[i] || 0)
          const p = Number(prices[i] ?? prices[prices.length - 1] ?? it?.unitPrice ?? 0)
          const v = Number(vols[i] ?? vols[vols.length - 1] ?? 0)
          out.push({
            key: `${ns?.id || ns?.nsNo}-${it?.id || itemName}-split-${i}`,
            ...base,
            departmentName,
            categoryName,
            itemName,
            isProduct,
            quantity: q,
            volume: Number.isFinite(v) ? v : '-',
            unitPrice: p,
            totalValue: q * p,
            isTaxable: !!it?.isTaxable
          })
        }
      } else {
        const q = Number(it?.quantity || 0)
        const p = Number(it?.unitPrice || 0)
        const v = Number(it?.variantDetails || 0)
        out.push({
          key: `${ns?.id || ns?.nsNo}-${it?.id || itemName}-single`,
          ...base,
          departmentName,
          categoryName,
          itemName,
          isProduct,
          quantity: q,
          volume: Number.isFinite(v) && v > 0 ? v : '-',
          unitPrice: p,
          totalValue: q * p,
          isTaxable: !!it?.isTaxable
        })
      }
    }
  }
  return out
})

function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

const sortedRows = computed(() => {
  const list = [...flattenedRows.value]
  const modifier = sortOrder.value === 'asc' ? 1 : -1

  const getVal = (row) => {
    if (sortKey.value === 'isTaxable') return row.isTaxable ? 1 : 0
    return row[sortKey.value]
  }

  list.sort((a, b) => {
    const va = getVal(a)
    const vb = getVal(b)
    const na = typeof va === 'number' ? va : (va !== null && va !== undefined && va !== '' ? Number(va) : NaN)
    const nb = typeof vb === 'number' ? vb : (vb !== null && vb !== undefined && vb !== '' ? Number(vb) : NaN)
    if (!Number.isNaN(na) && !Number.isNaN(nb)) {
      return (na - nb) * modifier
    }
    const sa = (va ?? '').toString().toLowerCase()
    const sb = (vb ?? '').toString().toLowerCase()
    if (sa < sb) return -1 * modifier
    if (sa > sb) return 1 * modifier
    return 0
  })

  return list
})

const totalPriceSum = computed(() => {
  const sum = flattenedRows.value.reduce((acc, row) => acc + Number(row?.totalValue || 0), 0)
  return Number.isFinite(sum) ? sum.toFixed(2) : '0.00'
})

function handlePrintReport() {
  if (!flattenedRows.value.length) {
    alert('No data to print. Please generate the report first.')
    return
  }
  window.print()
}

onMounted(async () => {
  await loadLookups()
  await loadProductsByDepartment()
})

watch(() => filters.value.departmentId, async () => {
  await loadProductsByDepartment()
})

watch(() => filters.value.ingredientFilter, async () => {
  await loadProductsByDepartment()
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 11px;
  line-height: 1.15;
  color: #1e293b;
}
th, td {
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  padding: 3px 4px;
  font-size: inherit;
}
th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

.nonstockable-table-wrap {
  min-height: 70vh;
}

@media print {
  :global(.sidebar),
  :global(header),
  :global(nav),
  :global(.top-bar),
  :global(.app-header),
  :global(.logout-btn) {
    display: none !important;
  }

  .print-hide { display: none !important; }

  body, .bg-\[\#f8fafc\] {
    background: transparent !important;
  }

  .overflow-x-auto {
    overflow: visible !important;
  }

  .print-container {
    box-shadow: none !important;
    border: none !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  .print-table {
    width: 100% !important;
    border-collapse: collapse !important;
    border-spacing: 0 !important;
    table-layout: auto !important;
  }

  .print-table th, .print-table td {
    border: 0 !important;
    border-right: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
    padding: 4px !important;
    text-align: left !important;
    color: #000 !important;
    font-size: 10px !important;
    white-space: normal !important;
    word-break: break-word !important;
  }

  .print-table tr > *:first-child {
    border-left: 1px solid #000 !important;
  }

  .print-table thead tr:first-child > * {
    border-top: 1px solid #000 !important;
  }

  .print-table th {
    background-color: #f3f3f3 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .print-doc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  .print-doc-header-grid {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    column-gap: 12px;
  }

  .print-doc-left {
    justify-self: start;
  }

  .print-doc-title {
    justify-self: center;
    margin: 0 !important;
    white-space: nowrap;
  }

  .print-doc-right {
    justify-self: end;
  }

  .print-company-block {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .print-company-logo {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    display: block;
    object-fit: contain;
    border: 0 !important;
    border-radius: 0;
    padding: 0;
    background: #fff;
  }

  .print-company-logo-fallback {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    border: 1px solid #000;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: #334155;
    background: #fff;
  }

  .print-company-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .print-company-name {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.2;
  }

  .print-doc-meta {
    font-size: 12px;
    color: #0f172a;
    white-space: nowrap;
  }
}
</style>
